// =============================================================================
// API CLIENT
// =============================================================================

import type { LinkPreviewData } from '@chlann/shared';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// =============================================================================
// TYPES
// =============================================================================

interface ApiError {
  code: string;
  message: string;
  details?: unknown;
}

interface ApiResponse<T> {
  data?: T;
  error?: ApiError;
}

// Shared content (in-chat Media / Links / Docs). The attachment item mirrors the
// getMessages attachment DTO plus the message fields the panel needs.
export interface ConversationMediaItem {
  id: string;
  type: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  thumbnailUrl: string | null;
  url: string;
  width?: number;
  height?: number;
  durationSeconds?: number;
  createdAt: string;
  messageId: string;
  senderId: string;
}

export interface ConversationLinkItem {
  url: string;
  messageId: string;
  senderId: string;
  createdAt: string;
}

export interface SharedContentPage<T> {
  items: T[];
  hasMore: boolean;
  nextCursor: string | null;
}

// =============================================================================
// CLIENT
// =============================================================================

class ApiClient {
  private baseUrl: string;
  private accessToken: string | null = null;
  private _refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
  }

  getAccessToken(): string | null {
    return this.accessToken;
  }

  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: { skipAuth?: boolean; _isRetry?: boolean }
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {};

    // Only set Content-Type if we have a body to send
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    // Send the User-Agent explicitly so the backend can name the device
    // ("Chrome on Windows") on login and reuse the same user_devices row on
    // re-login. CORS reflects request headers here (no fixed allow-list).
    if (typeof navigator !== 'undefined' && navigator.userAgent) {
      headers['x-device-name'] = navigator.userAgent;
    }

    if (this.accessToken && !options?.skipAuth) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }

    try {
      const response = await fetch(`${this.baseUrl}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        credentials: 'include', // For cookies (refresh token)
      });

      const data = await response.json();

      if (!response.ok) {
        const error = data.error || { code: 'UNKNOWN_ERROR', message: 'An error occurred' };

        // Auto-refresh on 401 (skip for auth endpoints and retries)
        if (response.status === 401 && !options?.skipAuth && !options?._isRetry) {
          const refreshed = await this._autoRefresh();
          if (refreshed) {
            return this.request<T>(method, path, body, { ...options, _isRetry: true });
          }
        }

        return { error };
      }

      return { data };
    } catch {
      return {
        error: {
          code: 'NETWORK_ERROR',
          message: 'Network error. Please check your connection.',
        },
      };
    }
  }

  /**
   * Force a token refresh outside the 401 interceptor path. Used by the
   * socket service to self-heal an auth-rejected reconnection, where there
   * is no REST response to trigger _autoRefresh. Shares the same
   * deduplicated refresh promise.
   */
  async refreshAccessToken(): Promise<boolean> {
    return this._autoRefresh();
  }

  // Deduplicated token refresh - concurrent 401s share one refresh attempt
  private async _autoRefresh(): Promise<boolean> {
    if (!this._refreshPromise) {
      this._refreshPromise = (async () => {
        try {
          const result = await this.refreshToken();
          if (result.data) {
            this.setAccessToken(result.data.accessToken);
            return true;
          }
          return false;
        } finally {
          this._refreshPromise = null;
        }
      })();
    }
    return this._refreshPromise;
  }

  // ---------------------------------------------------------------------------
  // AUTH
  // ---------------------------------------------------------------------------

  async register(input: {
    email?: string;
    phone?: string;
    password: string;
    displayName: string;
    language?: string;
  }) {
    return this.request<{
      message: string;
    }>('POST', '/api/auth/register', input, { skipAuth: true });
  }

  async login(input: {
    email?: string;
    phone?: string;
    password: string;
    /**
     * Existing deviceId persisted from a prior login. Passed through to
     * the backend so it can reuse the same user_devices row instead of
     * creating a fresh one (which would invalidate the Signal session
     * stored in IndexedDB against the old deviceId).
     */
    deviceId?: string;
  }) {
    return this.request<
      | { requires2FA: true; challengeId: string; twoFactorMethod: 'email' | 'sms' }
      | {
          accessToken: string;
          user: {
            id: string;
            email: string | null;
            phone: string | null;
            displayName: string;
            avatarUrl: string | null;
            language: string;
            uiMode: string;
            uiScale: number;
            uiContrast: boolean;
            uiMotion: string;
            hasCompletedOnboarding: boolean;
          };
          deviceId: string;
        }
    >('POST', '/api/auth/login', input, { skipAuth: true });
  }

  async verify2FA(challengeId: string, code: string, deviceId?: string) {
    return this.request<{
      accessToken: string;
      user: {
        id: string;
        email: string | null;
        phone: string | null;
        displayName: string;
        avatarUrl: string | null;
        language: string;
        uiMode: string;
        uiScale: number;
        uiContrast: boolean;
        uiMotion: string;
        hasCompletedOnboarding: boolean;
      };
      deviceId: string;
    }>(
      'POST',
      '/api/auth/2fa/verify',
      { challengeId, code, ...(deviceId ? { deviceId } : {}) },
      { skipAuth: true }
    );
  }

  async refreshToken() {
    return this.request<{ accessToken: string }>('POST', '/api/auth/refresh', undefined, {
      skipAuth: true,
    });
  }

  async logout() {
    return this.request<{ success: boolean }>('POST', '/api/auth/logout');
  }

  async forgotPassword(email: string) {
    return this.request<{
      message: string;
      challengeId?: string;
    }>('POST', '/api/auth/forgot-password', { email }, { skipAuth: true });
  }

  async resetPassword(challengeId: string, code: string, newPassword: string) {
    return this.request<{
      success: boolean;
      message: string;
    }>('POST', '/api/auth/reset-password', { challengeId, code, newPassword }, { skipAuth: true });
  }

  async submitWaitlist(input: {
    email: string;
    displayName: string;
    familyContext?: string;
    language?: 'en' | 'ga';
  }) {
    return this.request<{
      success: boolean;
      alreadyWaitlisted: boolean;
    }>('POST', '/api/waitlist', input, { skipAuth: true });
  }

  async getMe() {
    return this.request<{
      id: string;
      email: string | null;
      phone: string | null;
      displayName: string;
      avatarUrl: string | null;
      statusMessage: string | null;
      language: string;
      uiMode: string;
      uiScale: number;
      uiContrast: boolean;
      uiMotion: string;
      twoFactorEnabled: boolean;
      emailVerified: boolean;
      hasCompletedOnboarding: boolean;
      isAdmin: boolean;
      createdAt: string;
      privacy: {
        showOnlineStatus: boolean;
        showLastSeen: boolean;
        showReadReceipts: boolean;
        showTypingIndicator: boolean;
      } | null;
    }>('GET', '/api/auth/me');
  }

  async completeOnboarding() {
    return this.request<{ success: boolean; hasCompletedOnboarding: boolean }>(
      'PATCH',
      '/api/users/me/onboarding'
    );
  }

  // ---------------------------------------------------------------------------
  // CONVERSATIONS
  // ---------------------------------------------------------------------------

  async getConversations() {
    return this.request<{
      conversations: Array<{
        id: string;
        type: string;
        name: string | null;
        avatarUrl: string | null;
        lastMessage: {
          id: string;
          senderId: string;
          senderName: string;
          content: string;
          messageType: string;
          createdAt: string;
        } | null;
        unreadCount: number;
        isPinned: boolean;
        isMuted: boolean;
        mutedUntil: string | null;
        updatedAt: string;
        members: Array<{
          userId: string;
          displayName: string;
          avatarUrl: string | null;
          isBot: boolean;
        }>;
      }>;
    }>('GET', '/api/conversations');
  }

  async createConversation(input: {
    type: 'direct' | 'group' | 'subgroup';
    name?: string;
    memberIds: string[];
    familyId?: string;
    isAnnouncement?: boolean;
  }) {
    return this.request<{ conversationId: string }>('POST', '/api/conversations', input);
  }

  async pinConversation(id: string, pinned: boolean) {
    return this.request<{ success: boolean; isPinned: boolean }>(
      'PATCH',
      `/api/conversations/${id}/pin`,
      { pinned }
    );
  }

  async muteConversation(id: string, muteDuration: 'forever' | '8h' | '1d' | '7d' | null) {
    return this.request<{ success: boolean; mutedUntil: string | null }>(
      'PATCH',
      `/api/conversations/${id}/mute`,
      { muteDuration }
    );
  }

  async updateConversation(
    id: string,
    updates: {
      name?: string;
      description?: string;
      settings?: {
        // Pass a positive seconds value to enable disappearing messages, or
        // null to turn it off (the server schema is positive().nullable()).
        disappearingMessagesDuration?: number | null;
        isAnnouncement?: boolean;
      };
    }
  ) {
    return this.request<{ success: boolean }>('PATCH', `/api/conversations/${id}`, updates);
  }

  async addConversationMembers(id: string, userIds: string[]) {
    return this.request<{ success: boolean; addedCount: number }>(
      'POST',
      `/api/conversations/${id}/members`,
      { userIds }
    );
  }

  async removeConversationMember(id: string, userId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/api/conversations/${id}/members/${userId}`
    );
  }

  async getConversation(id: string) {
    return this.request<{
      id: string;
      type: string;
      name: string | null;
      description: string | null;
      avatarUrl: string | null;
      familyId: string | null;
      isAnnouncement: boolean;
      disappearingMessagesDuration: number | null;
      createdBy: string;
      createdAt: string;
      updatedAt: string;
      members: Array<{
        userId: string;
        displayName: string;
        avatarUrl: string | null;
        role: string;
        joinedAt: string;
      }>;
    }>('GET', `/api/conversations/${id}`);
  }

  // ---------------------------------------------------------------------------
  // MESSAGES
  // ---------------------------------------------------------------------------

  async getMessages(conversationId: string, options?: { before?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.before) params.set('before', options.before);
    if (options?.limit) params.set('limit', options.limit.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      messages: Array<{
        id: string;
        conversationId: string;
        senderId: string;
        senderDeviceId: string | null; // For Signal Protocol decryption
        senderName: string;
        senderAvatarUrl: string | null;
        messageType: string;
        content: string | null; // Plaintext content for bot messages, null for encrypted user messages
        replyToMessageId: string | null;
        replyToPreview: {
          senderId: string;
          senderName: string;
          messageType: string;
        } | null;
        hasAttachments: boolean;
        isForwarded: boolean;
        createdAt: string;
        editedAt: string | null;
        deletedAt: string | null;
        expiresAt: string | null;
        attachments: Array<{
          id: string;
          type: string;
          filename: string;
          mimeType: string;
          sizeBytes: number;
          thumbnailUrl: string | null;
          url: string;
          width?: number;
          height?: number;
          durationSeconds?: number;
        }>;
        reactions: Array<{
          emoji: string;
          userIds: string[];
          count: number;
        }>;
        linkPreview?: LinkPreviewData;
      }>;
      hasMore: boolean;
    }>('GET', `/api/messages/${conversationId}${query}`);
  }

  // Server-side unfurl of a pasted URL. Returns the preview, or { preview: null }
  // when the fetch failed closed (bad site, private target, timeout). Callers
  // treat a null preview as "no card" - never a send blocker.
  async getLinkPreview(url: string, conversationId: string) {
    return this.request<LinkPreviewData | { preview: null }>(
      'POST',
      '/api/messages/link-preview',
      { url, conversationId }
    );
  }

  async getConversationMedia(conversationId: string, options?: { before?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.before) params.set('before', options.before);
    if (options?.limit) params.set('limit', options.limit.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<SharedContentPage<ConversationMediaItem>>(
      'GET',
      `/api/messages/${conversationId}/media${query}`
    );
  }

  async getConversationDocs(conversationId: string, options?: { before?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.before) params.set('before', options.before);
    if (options?.limit) params.set('limit', options.limit.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<SharedContentPage<ConversationMediaItem>>(
      'GET',
      `/api/messages/${conversationId}/docs${query}`
    );
  }

  async getConversationLinks(conversationId: string, options?: { before?: string; limit?: number }) {
    const params = new URLSearchParams();
    if (options?.before) params.set('before', options.before);
    if (options?.limit) params.set('limit', options.limit.toString());

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<SharedContentPage<ConversationLinkItem>>(
      'GET',
      `/api/messages/${conversationId}/links${query}`
    );
  }

  async sendMessage(input: {
    conversationId: string;
    messageType: string;
    encryptedContent: Record<string, string>;
    replyToMessageId?: string;
    isForwarded?: boolean;
    attachments?: Array<{
      type: 'image' | 'video' | 'voice' | 'file';
      storageKey: string;
      thumbnailKey?: string;
      filename: string;
      mimeType: string;
      sizeBytes: number;
      width?: number;
      height?: number;
      durationSeconds?: number;
    }>;
    linkPreview?: LinkPreviewData;
  }) {
    return this.request<{ messageId: string }>('POST', '/api/messages', input);
  }

  async getMessageContent(conversationId: string, messageId: string) {
    return this.request<{
      encryptedContent: string;
      linkPreview: LinkPreviewData | null;
    }>('GET', `/api/messages/${conversationId}/${messageId}/content`);
  }

  async addReaction(messageId: string, emoji: string) {
    return this.request<{ success: boolean }>('POST', `/api/messages/${messageId}/reactions`, {
      emoji,
    });
  }

  async removeReaction(messageId: string, emoji: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/api/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`
    );
  }

  async deleteMessage(messageId: string, forEveryone: boolean) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/api/messages/${messageId}?forEveryone=${forEveryone}`
    );
  }

  async editMessage(messageId: string, encryptedContent: Record<string, string>) {
    return this.request<{ success: boolean }>(
      'PATCH',
      `/api/messages/${messageId}`,
      { encryptedContent }
    );
  }

  async markMessagesAsRead(conversationId: string, messageIds: string[]) {
    return this.request<{ success: boolean }>('POST', '/api/messages/read', {
      conversationId,
      messageIds,
    });
  }

  async getMemories(conversationId: string) {
    return this.request<{
      memories: Array<{
        id: string;
        senderName: string;
        senderAvatarUrl: string | null;
        messageType: string;
        content: string | null;
        hasAttachments: boolean;
        thumbnailUrl: string | null;
        createdAt: string;
        yearsAgo: number;
      }>;
    }>('GET', `/api/messages/${conversationId}/memories`);
  }

  async searchMessages(query: string, options?: { conversationId?: string; limit?: number }) {
    const params = new URLSearchParams();
    params.set('q', query);
    if (options?.conversationId) params.set('conversationId', options.conversationId);
    if (options?.limit) params.set('limit', options.limit.toString());

    return this.request<{
      results: Array<{
        messageId: string;
        conversationId: string;
        conversationName: string | null;
        conversationType: string;
        senderId: string;
        senderName: string;
        messageType: string;
        contentPreview: string;
        createdAt: string;
        highlightedText: string;
      }>;
      query: string;
    }>('GET', `/api/messages/search?${params.toString()}`);
  }

  // ---------------------------------------------------------------------------
  // USER PROFILE & SETTINGS
  // ---------------------------------------------------------------------------

  async getProfile() {
    return this.request<{
      user: {
        id: string;
        email: string | null;
        phone: string | null;
        displayName: string;
        avatarUrl: string | null;
        statusMessage: string | null;
        language: string;
        uiMode: string;
        uiScale: number;
        uiContrast: boolean;
        uiMotion: string;
        emailVerified: boolean;
        twoFactorEnabled: boolean;
        createdAt: string;
      };
      privacySettings: {
        showOnlineStatus: boolean;
        showLastSeen: boolean;
        showReadReceipts: boolean;
        showTypingIndicator: boolean;
      };
    }>('GET', '/api/users/me');
  }

  async updateProfile(updates: {
    displayName?: string;
    statusMessage?: string | null;
    language?: 'en' | 'ga';
    uiMode?: 'standard' | 'simplified' | 'accessibility';
    uiScale?: number;
    uiContrast?: boolean;
    uiMotion?: 'full' | 'reduced';
  }) {
    return this.request<{
      user: {
        id: string;
        displayName: string;
        statusMessage: string | null;
        language: string;
        uiMode: string;
        uiScale: number;
        uiContrast: boolean;
        uiMotion: string;
        avatarUrl: string | null;
      };
    }>('PATCH', '/api/users/me', updates);
  }

  async getPrivacySettings() {
    return this.request<{
      privacySettings: {
        showOnlineStatus: boolean;
        showLastSeen: boolean;
        showReadReceipts: boolean;
        showTypingIndicator: boolean;
      };
    }>('GET', '/api/users/me/privacy');
  }

  async updatePrivacySettings(updates: {
    showOnlineStatus?: boolean;
    showLastSeen?: boolean;
    showReadReceipts?: boolean;
    showTypingIndicator?: boolean;
  }) {
    return this.request<{
      privacySettings: {
        showOnlineStatus: boolean;
        showLastSeen: boolean;
        showReadReceipts: boolean;
        showTypingIndicator: boolean;
      };
    }>('PATCH', '/api/users/me/privacy', updates);
  }

  async removeAvatar() {
    return this.request<{ success: boolean }>('DELETE', '/api/users/me/avatar');
  }

  async uploadAvatar(file: File): Promise<ApiResponse<{
    avatarUrl: string;
    thumbnailUrl: string | null;
  }>> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(`${this.baseUrl}/api/users/me/avatar`, {
        method: 'POST',
        headers: {
          ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
        },
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        return { data: undefined, error: data.error || { code: 'UPLOAD_FAILED', message: 'Avatar upload failed' } };
      }
      return { data, error: undefined };
    } catch {
      return { data: undefined, error: { code: 'NETWORK_ERROR', message: 'Network error' } };
    }
  }

  async getUserProfile(userId: string) {
    return this.request<{
      user: {
        id: string;
        displayName: string;
        avatarUrl: string | null;
        statusMessage: string | null;
      };
    }>('GET', `/api/users/${userId}`);
  }

  async searchUsers(query?: string, limit = 20) {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    params.set('limit', limit.toString());

    return this.request<{
      users: Array<{
        id: string;
        displayName: string;
        avatarUrl: string | null;
        isBot: boolean;
      }>;
    }>('GET', `/api/users/search?${params.toString()}`);
  }

  // ---------------------------------------------------------------------------
  // FAMILIES
  // ---------------------------------------------------------------------------

  async getFamilies() {
    return this.request<{
      families: Array<{
        id: string;
        name: string;
        description: string | null;
        avatarUrl: string | null;
        role: string;
        memberCount: number;
        members: Array<{
          id: string;
          displayName: string;
          avatarUrl: string | null;
          role: string;
        }>;
        joinedAt: string;
        createdAt: string;
      }>;
    }>('GET', '/api/families');
  }

  async createFamily(input: { name: string; description?: string }) {
    return this.request<{
      family: {
        id: string;
        name: string;
        description: string | null;
        inviteCode: string;
        conversationId: string;
        createdAt: string;
      };
    }>('POST', '/api/families', input);
  }

  async getFamily(familyId: string) {
    return this.request<{
      family: {
        id: string;
        name: string;
        description: string | null;
        avatarUrl: string | null;
        inviteCode?: string;
        myRole: string;
        members: Array<{
          id: string;
          displayName: string;
          avatarUrl: string | null;
          statusMessage: string | null;
          role: string;
          joinedAt: string;
          lastSeenAt: string | null;
        }>;
        conversations: Array<{
          id: string;
          name: string | null;
          type: string;
        }>;
        createdAt: string;
      };
    }>('GET', `/api/families/${familyId}`);
  }

  async updateFamily(familyId: string, updates: { name?: string; description?: string | null }) {
    return this.request<{
      family: {
        id: string;
        name: string;
        description: string | null;
      };
    }>('PATCH', `/api/families/${familyId}`, updates);
  }

  async regenerateInviteCode(familyId: string) {
    return this.request<{ inviteCode: string }>('POST', `/api/families/${familyId}/invite`);
  }

  async joinFamily(inviteCode: string) {
    return this.request<{
      family: {
        id: string;
        name: string;
        description: string | null;
      };
    }>('POST', '/api/families/join', { inviteCode });
  }

  async removeFamilyMember(familyId: string, userId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/api/families/${familyId}/members/${userId}`
    );
  }

  async updateFamilyMemberRole(familyId: string, userId: string, role: string) {
    return this.request<{ success: boolean; role: string }>(
      'PATCH',
      `/api/families/${familyId}/members/${userId}`,
      { role }
    );
  }

  // ---------------------------------------------------------------------------
  // CALLS
  // ---------------------------------------------------------------------------

  async initiateCall(input: { conversationId: string; callType: 'voice' | 'video' }) {
    return this.request<{
      call: {
        id: string;
        conversationId: string;
        callType: 'voice' | 'video';
        status: string;
        createdAt: string;
      };
      participants: Array<{
        userId: string;
        displayName: string;
        avatarUrl: string | null;
      }>;
    }>('POST', '/api/calls', input);
  }

  async getCallHistory(options?: { limit?: number; before?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.before) params.set('before', options.before);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      calls: Array<{
        id: string;
        conversationId: string;
        conversationName: string | null;
        conversationAvatarUrl: string | null;
        callType: 'voice' | 'video';
        status: string;
        initiatedBy: string;
        initiatorName: string;
        participants: Array<{
          userId: string;
          displayName: string;
          avatarUrl: string | null;
          joinedAt: string | null;
          leftAt: string | null;
        }>;
        startedAt: string | null;
        endedAt: string | null;
        durationSeconds: number | null;
        wasInitiator: boolean;
        wasAnswered: boolean;
        createdAt: string;
      }>;
      hasMore: boolean;
    }>('GET', `/api/calls${query}`);
  }

  async getCallDetails(callId: string) {
    return this.request<{
      call: {
        id: string;
        conversationId: string;
        callType: 'voice' | 'video';
        status: string;
        initiatedBy: string;
        initiatorName: string;
        participants: Array<{
          userId: string;
          displayName: string;
          avatarUrl: string | null;
          joinedAt: string | null;
          leftAt: string | null;
          wasVideoEnabled: boolean;
        }>;
        startedAt: string | null;
        endedAt: string | null;
        durationSeconds: number | null;
        createdAt: string;
      };
    }>('GET', `/api/calls/${callId}`);
  }

  async acceptCall(callId: string) {
    return this.request<{ success: boolean }>('POST', `/api/calls/${callId}/accept`);
  }

  async declineCall(callId: string) {
    return this.request<{ success: boolean }>('POST', `/api/calls/${callId}/decline`);
  }

  async endCall(callId: string) {
    return this.request<{ durationSeconds: number }>('POST', `/api/calls/${callId}/end`);
  }

  async getTURNCredentials() {
    return this.request<{
      iceServers: Array<{
        urls: string | string[];
        username?: string;
        credential?: string;
      }>;
    }>('GET', '/api/calls/credentials');
  }

  // ---------------------------------------------------------------------------
  // PHOTOS
  // ---------------------------------------------------------------------------

  async getPhotos(options?: { limit?: number; before?: string; familyId?: string; albumId?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.before) params.set('before', options.before);
    if (options?.familyId) params.set('familyId', options.familyId);
    if (options?.albumId) params.set('albumId', options.albumId);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      photos: Array<{
        id: string;
        storageKey: string;
        thumbnailKey: string | null;
        originalFilename: string | null;
        mimeType: string;
        sizeBytes: number;
        width: number | null;
        height: number | null;
        takenAt: string | null;
        uploadedAt: string;
        uploader: {
          id: string;
          displayName: string;
          avatarUrl: string | null;
        };
        family: { id: string; name: string } | null;
      }>;
      hasMore: boolean;
      albumId?: string;
    }>('GET', `/api/photos${query}`);
  }

  async getPhotoDetails(photoId: string) {
    return this.request<{
      photo: {
        id: string;
        storageKey: string;
        thumbnailKey: string | null;
        originalFilename: string | null;
        mimeType: string;
        sizeBytes: number;
        width: number | null;
        height: number | null;
        takenAt: string | null;
        uploadedAt: string;
        uploader: {
          id: string;
          displayName: string;
          avatarUrl: string | null;
        };
        family: { id: string; name: string } | null;
      };
    }>('GET', `/api/photos/${photoId}`);
  }

  async uploadPhotos(
    files: Array<{ file: File; takenAt: string | null }>,
    familyId?: string,
  ): Promise<ApiResponse<{
    photos: Array<{
      id: string;
      storageKey: string;
      thumbnailKey: string | null;
      mimeType: string;
      sizeBytes: number;
      width: number | null;
      height: number | null;
      takenAt: string | null;
      filename: string;
    }>;
  }>> {
    const formData = new FormData();
    if (familyId) {
      formData.append('familyId', familyId);
    }
    // The server consumes each `takenAt` field for the `files` part that
    // immediately follows it, so order matters here.
    for (const { file, takenAt } of files) {
      if (takenAt) {
        formData.append('takenAt', takenAt);
      }
      formData.append('files', file);
    }

    try {
      const response = await fetch(`${this.baseUrl}/api/photos/upload`, {
        method: 'POST',
        headers: {
          ...(this.accessToken ? { Authorization: `Bearer ${this.accessToken}` } : {}),
        },
        credentials: 'include',
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        return { data: undefined, error: data.error || { code: 'UPLOAD_FAILED', message: 'Upload failed' } };
      }
      return { data, error: undefined };
    } catch {
      return { data: undefined, error: { code: 'NETWORK_ERROR', message: 'Network error' } };
    }
  }

  async deletePhoto(photoId: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/photos/${photoId}`);
  }

  async shareAttachmentToGallery(attachmentId: string, familyId: string, albumId?: string) {
    return this.request<{
      photo: {
        id: string;
        storageKey: string;
        thumbnailKey: string | null;
        originalFilename: string | null;
        mimeType: string;
        sizeBytes: number;
        width: number | null;
        height: number | null;
        uploadedAt: string;
        familyId: string | null;
      };
    }>('POST', '/api/photos/share-from-chat', { attachmentId, familyId, albumId });
  }

  // ---------------------------------------------------------------------------
  // ALBUMS
  // ---------------------------------------------------------------------------

  async getAlbums() {
    return this.request<{
      albums: Array<{
        id: string;
        name: string;
        description: string | null;
        isShared: boolean;
        familyId: string | null;
        photoCount: number;
        coverPhoto: {
          id: string;
          storageKey: string;
          thumbnailKey: string | null;
        } | null;
        owner: {
          id: string;
          displayName: string;
        };
        createdAt: string;
        updatedAt: string;
      }>;
    }>('GET', '/api/photos/albums');
  }

  async createAlbum(input: { name: string; description?: string; familyId?: string; isShared?: boolean }) {
    return this.request<{
      album: {
        id: string;
        name: string;
        description: string | null;
        familyId: string | null;
        isShared: boolean;
        photoCount: number;
        coverPhoto: null;
        createdAt: string;
        updatedAt: string;
      };
    }>('POST', '/api/photos/albums', input);
  }

  async getAlbumDetails(albumId: string) {
    return this.request<{
      album: {
        id: string;
        name: string;
        description: string | null;
        familyId: string | null;
        isShared: boolean;
        photoCount: number;
        coverPhoto: {
          id: string;
          storageKey: string;
          thumbnailKey: string | null;
        } | null;
        owner: {
          id: string;
          displayName: string;
          avatarUrl: string | null;
        };
        photos: Array<{
          id: string;
          storageKey: string;
          thumbnailKey: string | null;
        }>;
        createdAt: string;
        updatedAt: string;
      };
    }>('GET', `/api/photos/albums/${albumId}`);
  }

  async updateAlbum(albumId: string, updates: { name?: string; description?: string | null; coverPhotoId?: string | null; isShared?: boolean }) {
    return this.request<{ success: boolean }>('PATCH', `/api/photos/albums/${albumId}`, updates);
  }

  async deleteAlbum(albumId: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/photos/albums/${albumId}`);
  }

  async addPhotosToAlbum(albumId: string, photoIds: string[]) {
    return this.request<{ added: number }>('POST', `/api/photos/albums/${albumId}/photos`, { photoIds });
  }

  async removePhotosFromAlbum(albumId: string, photoIds: string[]) {
    return this.request<{ removed: number }>('DELETE', `/api/photos/albums/${albumId}/photos`, { photoIds });
  }

  // ---------------------------------------------------------------------------
  // NOTIFICATIONS
  // ---------------------------------------------------------------------------

  async getNotifications(options?: { limit?: number; before?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.before) params.set('before', options.before);

    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      notifications: Array<{
        id: string;
        type: 'message' | 'call' | 'family' | 'photo' | 'system';
        title: string;
        body: string;
        data?: Record<string, unknown>;
        isRead: boolean;
        createdAt: string;
      }>;
      hasMore: boolean;
    }>('GET', `/api/notifications${query}`);
  }

  async getUnreadNotificationCount() {
    return this.request<{ count: number }>('GET', '/api/notifications/unread-count');
  }

  async markNotificationAsRead(notificationId: string) {
    return this.request<{ success: boolean }>('PATCH', `/api/notifications/${notificationId}/read`);
  }

  async markAllNotificationsAsRead() {
    return this.request<{ success: boolean; count: number }>('POST', '/api/notifications/mark-all-read');
  }

  async deleteNotification(notificationId: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/notifications/${notificationId}`);
  }

  async getNotificationPreferences() {
    return this.request<{
      preferences: {
        enablePush: boolean;
        enableInApp: boolean;
        muteAll: boolean;
        notifyMessages: boolean;
        notifyCalls: boolean;
        notifyFamilyUpdates: boolean;
        notifyPhotoUploads: boolean;
      };
    }>('GET', '/api/notifications/preferences');
  }

  async updateNotificationPreferences(updates: {
    enablePush?: boolean;
    enableInApp?: boolean;
    muteAll?: boolean;
    notifyMessages?: boolean;
    notifyCalls?: boolean;
    notifyFamilyUpdates?: boolean;
    notifyPhotoUploads?: boolean;
  }) {
    return this.request<{
      preferences: {
        enablePush: boolean;
        enableInApp: boolean;
        muteAll: boolean;
        notifyMessages: boolean;
        notifyCalls: boolean;
        notifyFamilyUpdates: boolean;
        notifyPhotoUploads: boolean;
      };
    }>('PATCH', '/api/notifications/preferences', updates);
  }

  async getVapidPublicKey() {
    // Must go through the api client (absolute API base), not a relative
    // fetch: in prod a relative /api/... path hits the SPA rewrite and returns
    // index.html, so json() throws and push silently never subscribes.
    return this.request<{ publicKey: string }>('GET', '/api/notifications/vapid-public-key');
  }

  async subscribeToPush(subscription: { endpoint: string; keys: { p256dh: string; auth: string } }) {
    return this.request<{ success: boolean }>('POST', '/api/notifications/push/subscribe', subscription);
  }

  async unsubscribeFromPush() {
    return this.request<{ success: boolean }>('DELETE', '/api/notifications/push/unsubscribe');
  }

  // ---------------------------------------------------------------------------
  // ML / PHOTO SEARCH
  // ---------------------------------------------------------------------------

  async searchPhotos(query: string, limit = 20) {
    const params = new URLSearchParams();
    params.set('q', query);
    params.set('limit', limit.toString());

    return this.request<{
      query: string;
      results: Array<{
        photoId: string;
        storageKey: string;
        thumbnailKey: string | null;
        similarity: number;
        tags: string[];
      }>;
      totalResults: number;
      fallback?: boolean;
    }>('GET', `/api/ml/search?${params.toString()}`);
  }

  async getSimilarPhotos(photoId: string, limit = 10) {
    const params = new URLSearchParams();
    params.set('limit', limit.toString());

    return this.request<{
      photoId: string;
      results: Array<{
        photoId: string;
        storageKey: string;
        thumbnailKey: string | null;
        similarity: number;
        tags: string[];
      }>;
      totalResults: number;
    }>('GET', `/api/ml/photos/${photoId}/similar?${params.toString()}`);
  }

  async getPhotoMlMetadata(photoId: string) {
    return this.request<{
      isAnalyzed: boolean;
      analyzedAt: string | null;
      dominantColors: string[];
      brightness: number | null;
      isBlurry: boolean | null;
      faces: Array<{
        id: string;
        boundingBox: { x: number; y: number; width: number; height: number };
        confidence: number;
        identity: { id: string; name: string } | null;
      }>;
      objects: Array<{
        id: string;
        label: string;
        confidence: number;
        boundingBox: { x: number; y: number; width: number; height: number };
      }>;
      tags: Array<{
        tag: string;
        source: string;
        confidence: number | null;
      }>;
    }>('GET', `/api/ml/photos/${photoId}/metadata`);
  }

  async analyzePhoto(photoId: string) {
    return this.request<{
      success: boolean;
      message: string;
    }>('POST', `/api/ml/photos/${photoId}/analyze`);
  }

  async getMlHealth() {
    return this.request<{
      status: 'healthy' | 'unavailable';
      queue: {
        pending: number;
        processing: number;
      };
    }>('GET', '/api/ml/health');
  }

  async getFaceIdentities() {
    return this.request<{
      identities: Array<{
        id: string;
        name: string;
        familyId: string;
        photoCount: number;
        createdAt: string;
      }>;
      unidentifiedCount: number;
    }>('GET', '/api/ml/faces');
  }

  async getPhotosOfPerson(identityId: string) {
    return this.request<{
      person: {
        id: string;
        name: string;
        photoCount: number;
      };
      photos: Array<{
        photoId: string;
        storageKey: string;
        thumbnailKey: string | null;
        uploadedAt: string;
        boundingBox: { x: number; y: number; width: number; height: number };
      }>;
    }>('GET', `/api/ml/faces/people/${identityId}/photos`);
  }

  async identifyFace(faceId: string, name: string, familyId: string) {
    return this.request<{
      success: boolean;
      identity: {
        id: string;
        name: string;
      };
    }>('POST', `/api/ml/faces/${faceId}/identify`, { name, familyId });
  }

  // ---------------------------------------------------------------------------
  // E2E ENCRYPTION / DEVICES
  // ---------------------------------------------------------------------------

  /**
   * Get list of user's devices
   */
  async getDevices(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      devices: Array<{
        id: string;
        deviceName: string | null;
        deviceType: string;
        hasSignalKeys: boolean;
        createdAt: string;
        lastActiveAt: string;
      }>;
      total: number;
      hasMore: boolean;
    }>('GET', `/api/devices${query}`);
  }

  /**
   * Register device Signal keys
   */
  async registerDeviceKeys(
    deviceId: string,
    keys: {
      identityPublicKey: string;
      signedPreKeyId: number;
      signedPreKeyPublic: string;
      signedPreKeySignature: string;
      preKeys: Array<{ keyId: number; publicKey: string }>;
    }
  ) {
    return this.request<{ success: boolean; message: string }>(
      'PUT',
      `/api/devices/${deviceId}/keys`,
      keys
    );
  }

  /**
   * Upload additional pre-keys
   */
  async uploadPreKeys(
    deviceId: string,
    preKeys: Array<{ keyId: number; publicKey: string }>
  ) {
    return this.request<{ success: boolean; preKeyCount: number }>(
      'POST',
      `/api/devices/${deviceId}/prekeys`,
      { preKeys }
    );
  }

  /**
   * Get pre-key count for current device
   */
  async getPreKeyCount() {
    return this.request<{ preKeyCount: number }>('GET', '/api/devices/prekey-count');
  }

  /**
   * Delete a device
   */
  async deleteDevice(deviceId: string) {
    return this.request<{ success: boolean; message: string }>(
      'DELETE',
      `/api/devices/${deviceId}`
    );
  }

  /**
   * Get pre-key bundle for a user's devices (for establishing encrypted session)
   */
  async getPreKeyBundle(userId: string, deviceId?: string) {
    const query = deviceId ? `?deviceId=${deviceId}` : '';
    return this.request<{
      bundles: Array<{
        deviceId: string;
        identityKey: string;
        signedPreKeyId: number;
        signedPreKey: string;
        signedPreKeySignature: string;
        preKey?: {
          keyId: number;
          publicKey: string;
        };
      }>;
    }>('GET', `/api/users/${userId}/prekey-bundle${query}`);
  }

  /**
   * Get devices for all members of a conversation (for E2E encryption)
   */
  async getConversationDevices(conversationId: string) {
    return this.request<{
      devices: Array<{
        userId: string;
        deviceId: string;
        deviceType: string;
        hasSignalKeys: boolean;
      }>;
    }>('GET', `/api/conversations/${conversationId}/devices`);
  }

  // ---------------------------------------------------------------------------
  // CALENDAR / EVENTS
  // ---------------------------------------------------------------------------

  async getEvents(options: { familyId: string; startDate: string; endDate: string }) {
    const params = new URLSearchParams();
    params.set('familyId', options.familyId);
    params.set('startDate', options.startDate);
    params.set('endDate', options.endDate);

    return this.request<{
      events: Array<{
        id: string;
        title: string;
        color: string;
        isAllDay: boolean;
        startAt: string;
        endAt: string;
        rsvpStatus: string | null;
        attendeeCount: number;
      }>;
    }>('GET', `/api/events?${params.toString()}`);
  }

  async getEventDetail(eventId: string) {
    return this.request<{
      event: {
        id: string;
        familyId: string;
        title: string;
        description: string | null;
        location: string | null;
        color: string;
        isAllDay: boolean;
        startAt: string;
        endAt: string;
        recurrence: string | null;
        recurrenceEndAt: string | null;
        createdBy: string;
        createdByName: string;
        attendees: Array<{
          userId: string;
          displayName: string;
          avatarUrl: string | null;
          rsvpStatus: string;
          rsvpAt: string | null;
        }>;
        reminderMinutes: number | null;
        createdAt: string;
        updatedAt: string;
      };
    }>('GET', `/api/events/${eventId}`);
  }

  async createEvent(input: {
    familyId: string;
    title: string;
    description?: string;
    location?: string;
    color?: string;
    isAllDay?: boolean;
    startAt: string;
    endAt: string;
    recurrence?: string;
    recurrenceEndAt?: string;
    reminderMinutes?: number;
  }) {
    return this.request<{
      event: {
        id: string;
        familyId: string;
        title: string;
        description: string | null;
        location: string | null;
        color: string;
        isAllDay: boolean;
        startAt: string;
        endAt: string;
        recurrence: string | null;
        recurrenceEndAt: string | null;
        createdBy: string;
        createdByName: string;
        attendees: Array<{
          userId: string;
          displayName: string;
          avatarUrl: string | null;
          rsvpStatus: string;
          rsvpAt: string | null;
        }>;
        reminderMinutes: number | null;
        createdAt: string;
        updatedAt: string;
      };
    }>('POST', '/api/events', input);
  }

  async updateEvent(eventId: string, updates: {
    title?: string;
    description?: string | null;
    location?: string | null;
    color?: string;
    isAllDay?: boolean;
    startAt?: string;
    endAt?: string;
    recurrence?: string | null;
    recurrenceEndAt?: string | null;
    reminderMinutes?: number | null;
  }) {
    return this.request<{
      event: {
        id: string;
        familyId: string;
        title: string;
        description: string | null;
        location: string | null;
        color: string;
        isAllDay: boolean;
        startAt: string;
        endAt: string;
        recurrence: string | null;
        recurrenceEndAt: string | null;
        createdBy: string;
        createdByName: string;
        attendees: Array<{
          userId: string;
          displayName: string;
          avatarUrl: string | null;
          rsvpStatus: string;
          rsvpAt: string | null;
        }>;
        reminderMinutes: number | null;
        createdAt: string;
        updatedAt: string;
      };
    }>('PATCH', `/api/events/${eventId}`, updates);
  }

  async deleteEvent(eventId: string) {
    return this.request<{ success: boolean }>('DELETE', `/api/events/${eventId}`);
  }

  async rsvpEvent(eventId: string, status: string) {
    return this.request<{ success: boolean }>('POST', `/api/events/${eventId}/rsvp`, { status });
  }

  // ---------------------------------------------------------------------------
  // GENERIC HTTP METHODS (for new endpoints)
  // ---------------------------------------------------------------------------

  async get<T = unknown>(path: string): Promise<T> {
    const response = await this.request<T>('GET', `/api${path}`);
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data as T;
  }

  async post<T = unknown>(path: string, body?: unknown): Promise<T> {
    const response = await this.request<T>('POST', `/api${path}`, body);
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data as T;
  }

  async put<T = unknown>(path: string, body?: unknown): Promise<T> {
    const response = await this.request<T>('PUT', `/api${path}`, body);
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data as T;
  }

  async delete<T = unknown>(path: string): Promise<T> {
    const response = await this.request<T>('DELETE', `/api${path}`);
    if (response.error) {
      throw new Error(response.error.message);
    }
    return response.data as T;
  }
}

// Export singleton instance
export const api = new ApiClient(API_BASE_URL);

/**
 * Append the current access token to a media URL as a `?token=` query
 * param so <img>, <video>, <audio> tags can authenticate. The browser
 * cannot decorate these tags with an Authorization header, so the
 * backend authHook accepts a token query param as a fallback for GETs.
 *
 * Tokens are 15-minute JWTs and the request logger redacts the param,
 * so they don't appear in access logs. They will appear in browser
 * history; that's an accepted trade-off for a same-device cache.
 *
 * Returns the URL unchanged if no token is set or if the URL already
 * carries a token.
 */
export function withAuthToken(url: string): string {
  const token = api.getAccessToken();
  if (!token || url.includes('token=')) return url;
  return url + (url.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(token);
}

/**
 * Authenticated media URL for a raw storage key (e.g. a link-preview thumbnail's
 * `imageKey`). The /api/media GET is auth-gated and an <img> cannot send the
 * bearer header, so the token rides in the query string via withAuthToken.
 */
export function mediaUrlFromKey(key: string): string {
  return withAuthToken(`${API_BASE_URL}/api/media/${key}`);
}
